'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';
import { classNames } from '../../utils';
import { AnalyticsService } from '../../services';
import { ANALYTICS_EVENT_TRIGGERS } from '../../utils/analytics/constants';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Analytics data attributes for modal tracking
   * Spread this object onto the modal container
   * @example {...getModalOpenConfig({...})}
   */
  'data-ga-config'?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  'data-ga-config': dataGaConfig,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (modal && isMounted) {
      if (isOpen) {
        if (!modal.open) {
          modal.showModal();
          document.body.style.overflow = 'hidden';

          // Track modal open event if analytics configured
          if (dataGaConfig) {
            const analyticsService = AnalyticsService.getInstance();
            analyticsService.trackFromElement(modal, ANALYTICS_EVENT_TRIGGERS.MODAL_OPEN);
          }
        }
      } else {
        if (modal.open) {
          modal.close();
          document.body.style.overflow = 'unset';
        }
      }
    }
  }, [isOpen, isMounted, dataGaConfig]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && event.target === modalRef.current) {
        // Track modal close event before calling onClose
        if (dataGaConfig && modalRef.current) {
          const analyticsService = AnalyticsService.getInstance();
          analyticsService.trackFromElement(modalRef.current, ANALYTICS_EVENT_TRIGGERS.MODAL_CLOSE);
        }
        onClose();
      }
    };

    if (isOpen && isMounted) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isMounted, dataGaConfig]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  const modalContent = (
    <dialog
      ref={modalRef}
      className={classNames(styles['modal-overlay'], className)}
      data-ga-config={dataGaConfig}
    >
      {children}
    </dialog>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};
